import {
  AlertTriangle,
  CheckCircle2,
  CreditCard,
  Landmark,
  Loader2,
  XCircle,
} from 'lucide-react';
import type { ReactNode } from 'react';

import { Checkbox } from '~shared/shadcn/ui/checkbox';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '~shared/shadcn/ui/tooltip';

import { LOCK_CHECKBOX_CLASS_NAME, PAYMENT_RUN_STATUS } from './constants';
import {
  isPaymentRunRowLockDisabled,
  isPaymentRunRowSelectedForLock,
} from './payment-run-review.helpers';
import type { PaymentRunReviewLockRowState } from './payment-run-review.types';
import { normalizeStatus } from './payment-run-status';

function FailedStatusWithReasonTooltip({
  failureReason,
  children,
}: {
  failureReason?: string | null;
  children: ReactNode;
}) {
  const text = failureReason?.trim();
  if (!text) {
    return <>{children}</>;
  }
  return (
    <TooltipProvider delayDuration={300}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span className='inline-flex max-w-full cursor-default'>
            {children}
          </span>
        </TooltipTrigger>
        <TooltipContent side='top' className='max-w-sm text-left'>
          <p className='text-xs leading-snug'>{text}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

/** Shown in the Payment column when no payment method is available. */
const PAYMENT_METHOD_EMPTY_LABEL = '-';

function normalizeCardLast4Display(raw: string | undefined): string {
  const digits = String(raw ?? '').replace(/\D/g, '');
  if (digits.length === 0) return '';
  return digits.length <= 4 ? digits : digits.slice(-4);
}

export function PaymentRunLockCheckbox({
  invoiceId,
  parentLabel,
  lockSelectionIds,
  incrementalLockAllowed,
  approvedForRunSet,
  lockableIds,
  lockInteractionsDisabled,
  onToggleLockSelection,
}: PaymentRunReviewLockRowState & {
  parentLabel: string;
  onToggleLockSelection: (invoiceId: string, checked: boolean) => void;
}) {
  const lockState = {
    invoiceId,
    lockSelectionIds,
    incrementalLockAllowed,
    approvedForRunSet,
    lockableIds,
    lockInteractionsDisabled,
  };

  return (
    <Checkbox
      checked={isPaymentRunRowSelectedForLock(lockState)}
      disabled={isPaymentRunRowLockDisabled(lockState)}
      onCheckedChange={(value) => {
        onToggleLockSelection(invoiceId, value === true);
      }}
      className={LOCK_CHECKBOX_CLASS_NAME}
      aria-label={`Select for approve and lock: ${parentLabel || 'charge'}`}
    />
  );
}

export function PaymentMethodCell({
  paymentMethod,
  cardLast4,
}: {
  paymentMethod?: string;
  cardLast4?: string;
}) {
  const trimmed = paymentMethod?.trim() ?? '';
  const label =
    trimmed.length > 0 ? trimmed : PAYMENT_METHOD_EMPTY_LABEL;
  const last4 = normalizeCardLast4Display(cardLast4);
  if (last4.length > 0) {
    return (
      <span>
        {label} - {last4}
      </span>
    );
  }
  return <span>{label}</span>;
}

export function StatusCell({
  status,
  failureReason,
}: {
  status: string;
  /** Shown in a hover tooltip for failed / charge-failed rows when present. */
  failureReason?: string | null;
}) {
  const s = normalizeStatus(status);
  if (s === PAYMENT_RUN_STATUS.READY) {
    return (
      <span className='inline-flex items-center gap-1.5 font-medium text-emerald-600'>
        <CheckCircle2 className='size-4' aria-hidden /> Ready for Processing
      </span>
    );
  }
  if (s === PAYMENT_RUN_STATUS.PROCESSING) {
    return (
      <span className='inline-flex items-center gap-1.5 font-medium text-sky-700'>
        <Loader2 className='size-4 animate-spin' aria-hidden /> Processing
      </span>
    );
  }
  if (s === PAYMENT_RUN_STATUS.FEE_CAP) {
    return (
      <span className='inline-flex items-center gap-1.5 font-medium text-amber-600'>
        <AlertTriangle className='size-4' aria-hidden /> Fee cap
      </span>
    );
  }
  if (s === PAYMENT_RUN_STATUS.CARD_CHARGED) {
    return (
      <span className='inline-flex items-center gap-1.5 font-medium text-blue-600'>
        <CreditCard className='size-4 shrink-0' aria-hidden /> Successful Charge
      </span>
    );
  }
  if (s === PAYMENT_RUN_STATUS.ACH_CHARGED) {
    return (
      <span className='inline-flex items-center gap-1.5 font-medium text-blue-600'>
        <Landmark className='size-4 shrink-0' aria-hidden /> ACH Charged
      </span>
    );
  }
  if (s === PAYMENT_RUN_STATUS.SUPERSEDED) {
    return (
      <span className='inline-flex items-center gap-1.5 font-medium text-amber-700'>
        <XCircle className='size-4' aria-hidden /> Superseded
      </span>
    );
  }
  if (s === PAYMENT_RUN_STATUS.DROPPED || s === PAYMENT_RUN_STATUS.DUPLICATE) {
    return (
      <span className='inline-flex items-center gap-1.5 font-medium text-destructive'>
        <XCircle className='size-4' aria-hidden /> {status}
      </span>
    );
  }
  if (s === PAYMENT_RUN_STATUS.CHARGE_FAILED) {
    return (
      <FailedStatusWithReasonTooltip failureReason={failureReason}>
        <span className='inline-flex items-center gap-1.5 font-medium text-destructive'>
          <XCircle className='size-4' aria-hidden /> Charge Failed
        </span>
      </FailedStatusWithReasonTooltip>
    );
  }
  if (s === PAYMENT_RUN_STATUS.FAILED) {
    return (
      <FailedStatusWithReasonTooltip failureReason={failureReason}>
        <span className='inline-flex items-center gap-1.5 font-medium text-destructive'>
          <XCircle className='size-4' aria-hidden /> Failed
        </span>
      </FailedStatusWithReasonTooltip>
    );
  }
  return <span className='font-medium'>{status || '—'}</span>;
}
